if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.submitButtonLabel = this.submitButton ? this.submitButton.querySelector('span') : null;
      this.originalLabelText = this.submitButtonLabel ? this.submitButtonLabel.textContent : '';
      this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.querySelector('.product-form__error-message');
      this.hideErrors = this.dataset.hideErrors === 'true';

      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.style.opacity = '1';
      }

      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    }

    onSubmitHandler(evt) {
      evt.preventDefault();

      // Double-submit guard — ignore taps while an add is already in flight.
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      // --- ML size gate (Jul 30 2026) ------------------------------------
      // The size radios render with Shopify's default variant PRE-CHECKED
      // (product-variant-options.liquid: value.selected => checked), and the
      // form always carries a hidden variant id — so without this gate a
      // shopper who never touched the pills silently adds the default size.
      // Block the add until a size was actively chosen; show why; scroll to
      // the pills. See window.mlSize at the end of this file.
      if (window.mlSize && !window.mlSize.chosen()) {
        this.showError('Please select a size');
        window.mlSize.prompt('main');
        return;
      }
      // -------------------------------------------------------------------

      this.hideError();

      // Instant feedback — runs synchronously on tap, before the network request.
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.style.opacity = '0.7';
      if (this.submitButtonLabel) this.submitButtonLabel.textContent = 'Adding…';

      const formData = new FormData(this.form);
      formData.append('sections', 'cart-drawer');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then((res) => res.json())
        .then((data) => {
          // /cart/add.js only returns a status field when the add FAILED (e.g. sold out).
          if (data.status) {
            this.showError(data.description || data.message || 'Could not add to bag.');
            return;
          }

          // --- ML cart-loss instrumentation (Jul 2026) ---------------------
          // The drawer HTML rides back in THIS request, so it always reflects
          // the cart the add just wrote to — a populated drawer proves nothing
          // about whether the cart cookie will survive the next navigation.
          // Record what was added, and flag the case where no cart cookie is
          // readable straight after a successful add (the suspected FB/IG
          // in-app browser failure). Must never break the add itself.
          // NOTE: data.quantity is the line's new TOTAL in cart, not a delta.
          try {
            const vid = data.variant_id || data.id;
            const lines = JSON.parse(sessionStorage.getItem('ml_added_lines') || '[]');
            const existing = lines.find((l) => l.id === vid);
            if (existing) { existing.qty = data.quantity; }
            else { lines.push({ id: vid, qty: data.quantity }); }
            sessionStorage.setItem('ml_added_lines', JSON.stringify(lines));
            sessionStorage.setItem('ml_added', '1');
            if (!/(^|;\s*)cart=/.test(document.cookie) && typeof clarity === 'function') {
              clarity('event', 'ml_add_no_cart_cookie');
              clarity('set', 'ml_cart_bug', 'no_cookie_after_add');
            }
          } catch (e) { /* instrumentation must never break the add */ }
          // -----------------------------------------------------------------

          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.renderContents(data);
        })
        .catch(() => {
          this.showError('Something went wrong. Please try again.');
        })
        .finally(() => {
          // Reset the button so it can be used again.
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.style.opacity = '1';
          if (this.submitButtonLabel) this.submitButtonLabel.textContent = this.originalLabelText;
        });
    }

    showError(message) {
      if (this.hideErrors) return;
      if (this.errorWrapper && this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorWrapper.removeAttribute('hidden');
      } else {
        console.error(message);
      }
    }

    hideError() {
      if (this.errorWrapper) this.errorWrapper.setAttribute('hidden', '');
    }
  });
}

// ============================================================
// ML SIZE GATE (Jul 30 2026) — window.mlSize
// One shared gate for every add-to-bag entry point. Tracks
// whether the shopper ACTIVELY chose a size (radio change or
// label click); ?variant= deep links (e.g. Meta catalog ads)
// count as chosen; products with no size radios are exempt.
// prompt() gives feedback that never depends on a scroll
// landing: visible message + explicit scrollTo + watchdog +
// pulse, and fires Clarity ml_size_gate so frequency of
// no-size clicks becomes measurable.
// Also takes over #ml-sticky-atc-btn via document-level
// CAPTURE (fires before the button's own listener in
// ml-custom.js, whose no-size branch gave no visible feedback
// — that old handler still binds but never runs).
// ============================================================
(function () {
  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    var pillWrap =
      document.querySelector('.product-form__input--pill') || document.querySelector('variant-selects');
    var radios = document.querySelectorAll(
      "variant-radios input[type='radio'], .product-form__input--pill input[type='radio']"
    );

    // No size options on this product => gate is inert. Deep-linked variant
    // (?variant=123) => the shopper arrived having chosen; don't punish ads.
    var chosen = !radios.length || /[?&]variant=\d/.test(location.search);

    function markChosen() {
      chosen = true;
      var msg = document.getElementById('ml-size-msg');
      if (msg) msg.style.display = 'none';
    }

    radios.forEach(function (r) {
      r.addEventListener('change', markChosen);
    });
    if (pillWrap) {
      pillWrap.addEventListener('click', function (ev) {
        if (ev.target && ev.target.closest && ev.target.closest('label')) markChosen();
      });
    }

    // Self-contained pulse keyframes so the pulse is visible even if no theme
    // CSS defines .ml-size-pulse.
    if (!document.getElementById('ml-size-gate-style')) {
      var st = document.createElement('style');
      st.id = 'ml-size-gate-style';
      st.textContent =
        '.ml-size-pulse{animation:mlSizePulse 1.1s ease 2;border-radius:8px}' +
        '@keyframes mlSizePulse{0%{box-shadow:0 0 0 0 rgba(158,76,88,.55)}70%{box-shadow:0 0 0 14px rgba(158,76,88,0)}100%{box-shadow:0 0 0 0 rgba(158,76,88,0)}}';
      document.head.appendChild(st);
    }

    var msgTimer = null;
    function showStickyMsg() {
      var bar = document.getElementById('ml-sticky-atc');
      if (!bar) return;
      var msg = document.getElementById('ml-size-msg');
      if (!msg) {
        msg = document.createElement('div');
        msg.id = 'ml-size-msg';
        msg.textContent = 'Select a size first';
        msg.style.cssText =
          'text-align:center;font-size:13px;letter-spacing:.04em;color:#9e4c58;background:#fbf4ef;padding:6px 10px;border-radius:6px;margin:0 0 6px;';
        bar.insertAdjacentElement('afterbegin', msg);
      }
      msg.style.display = 'block';
      if (msgTimer) clearTimeout(msgTimer);
      msgTimer = setTimeout(function () { msg.style.display = 'none'; }, 4000);
    }

    function prompt(context) {
      try {
        if (typeof clarity === 'function') {
          clarity('event', 'ml_size_gate');
          clarity('set', 'ml_size_gate_src', context || 'unknown');
        }
      } catch (e) { /* analytics must never break the gate */ }

      if (context === 'sticky') showStickyMsg();

      var target = pillWrap;
      if (!target) return;

      // Explicit scroll — scrollIntoView proved unreliable here (Jul 30 test:
      // pulse applied, page never moved). Put the pills a third of the way
      // down the viewport, and hard-jump if the smooth scroll doesn't take.
      var y = Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - window.innerHeight / 3);
      try { window.scrollTo({ top: y, behavior: 'smooth' }); } catch (e2) { window.scrollTo(0, y); }
      setTimeout(function () {
        if (Math.abs(window.pageYOffset - y) > 80) window.scrollTo(0, y);
      }, 650);

      target.classList.remove('ml-size-pulse');
      void target.offsetWidth;
      target.classList.add('ml-size-pulse');
    }

    window.mlSize = {
      chosen: function () { return chosen; },
      prompt: prompt
    };

    // Sticky pill takeover. Capture phase on document runs BEFORE the
    // button's own (target-phase) listener from ml-custom.js, so that old
    // handler is fully superseded for both branches.
    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('#ml-sticky-atc-btn') : null;
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        if (!chosen) {
          prompt('sticky');
          return;
        }
        var submit = document.querySelector('.product-form__submit');
        if (submit) submit.click();
        setTimeout(function () {
          var d = document.querySelector('cart-drawer');
          if (d && typeof d.open === 'function') d.open();
          else document.body.dispatchEvent(new CustomEvent('cart:open'));
        }, 500);
      },
      true
    );
  });
})();
